
import React from 'react';
import { Button } from '@/components/ui/button';
import { WidgetProps } from '@/lib/dashboard/types';
import {
  Plus,
  Play,
  Users,
  MessageCircle,
  Shield,
  BarChart3,
  Settings,
  Video,
  FileText,
  Eye
} from 'lucide-react';

/**
 * Quick Actions Widget
 * Provides role-based quick action buttons for common tasks
 */
export const QuickActionsWidget = React.memo(function QuickActionsWidget({ role, onAction }: WidgetProps) {
  const userRole = role;

  const getActionsForRole = (role: string) => {
    const commonActions = [
      {
        label: 'View Streams',
        icon: <Eye className="h-4 w-4" />,
        action: 'view_streams',
        variant: 'outline' as const
      }
    ];

    switch (role) {
      case 'admin':
        return [
          ...commonActions,
          {
            label: 'Add User',
            icon: <Plus className="h-4 w-4" />,
            action: 'add_user',
            variant: 'default' as const
          },
          {
            label: 'Create Stream',
            icon: <Video className="h-4 w-4" />,
            action: 'start_stream',
            variant: 'default' as const
          },
          {
            label: 'View Analytics',
            icon: <BarChart3 className="h-4 w-4" />,
            action: 'view_analytics',
            variant: 'outline' as const
          },
          {
            label: 'System Settings',
            icon: <Settings className="h-4 w-4" />,
            action: 'system_settings',
            variant: 'outline' as const
          }
        ];

      case 'media_team':
        return [
          ...commonActions,
          {
            label: 'Start Stream',
            icon: <Play className="h-4 w-4" />,
            action: 'start_stream',
            variant: 'default' as const
          },
          {
            label: 'Upload Recording',
            icon: <FileText className="h-4 w-4" />,
            action: 'upload_recording',
            variant: 'outline' as const
          },
          {
            label: 'Stream Analytics',
            icon: <BarChart3 className="h-4 w-4" />,
            action: 'view_analytics',
            variant: 'outline' as const
          }
        ];

      case 'moderator':
        return [
          ...commonActions,
          {
            label: 'Moderate Comments',
            icon: <MessageCircle className="h-4 w-4" />,
            action: 'moderate_comments',
            variant: 'default' as const
          },
          {
            label: 'Monitor Streams',
            icon: <Shield className="h-4 w-4" />,
            action: 'monitor_streams',
            variant: 'outline' as const
          },
          {
            label: 'View Reports',
            icon: <BarChart3 className="h-4 w-4" />,
            action: 'view_reports',
            variant: 'outline' as const
          }
        ];

      case 'pastor':
      default:
        return [
          ...commonActions,
          {
            label: 'Join Stream',
            icon: <Play className="h-4 w-4" />,
            action: 'join_stream',
            variant: 'default' as const
          },
          {
            label: 'My Comments',
            icon: <MessageCircle className="h-4 w-4" />,
            action: 'my_comments',
            variant: 'outline' as const
          }
        ];
    }
  };

  const actions = getActionsForRole(userRole || 'pastor');

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        {actions.map((action, index) => (
          <Button
            key={index}
            variant={action.variant}
            size="sm"
            onClick={() => onAction?.(action.action)}
            className="flex items-center gap-2 h-auto py-3 px-4 text-left justify-start"
          >
            {action.icon}
            <span className="text-sm">{action.label}</span>
          </Button>
        ))}
      </div>

      {/* Role-specific additional actions */}
      {userRole === 'admin' && (
        <div className="pt-4 border-t">
          <h4 className="text-sm font-medium mb-2">Administrative Actions</h4>
          <div className="grid grid-cols-1 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAction?.('bulk_user_import')}
              className="justify-start"
            >
              <Users className="h-4 w-4 mr-2" />
              Bulk User Import
            </Button>
          </div>
        </div>
      )}

      {userRole === 'moderator' && (
        <div className="pt-4 border-t">
          <h4 className="text-sm font-medium mb-2">Moderation Tools</h4>
          <div className="grid grid-cols-1 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAction?.('moderation_queue')}
              className="justify-start"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Full Moderation Queue
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onAction?.('view_reports')}
              className="justify-start"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Moderation Reports
            </Button>
          </div>
        </div>
      )}
    </div>
  );
});
